/**
 * =============================================================================
 * QUESTION FILTERS COMPONENT
 * =============================================================================
 *
 * Status filter and sort dropdowns for the question queue.
 *
 * EXTRACTED FROM: QuestionQueue.tsx (P1-17)
 *
 * =============================================================================
 */

import React from 'react';
import { Box, Checkbox, Flex, FlexItem, Input, Select } from '@bigcommerce/big-design';
import { SearchIcon } from '@bigcommerce/big-design-icons';
import { QUESTION_STATUS_LABELS } from '@find-my-part/types';

/**
 * Status filter options for the dropdown.
 */
interface StatusOption {
  value: string;
  content: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: '', content: 'All Statuses' },
  { value: 'PENDING_REVIEW', content: QUESTION_STATUS_LABELS.PENDING_REVIEW },
  { value: 'OPEN', content: QUESTION_STATUS_LABELS.OPEN },
  { value: 'IN_PROGRESS', content: QUESTION_STATUS_LABELS.IN_PROGRESS },
  { value: 'NEEDS_MORE_INFO', content: QUESTION_STATUS_LABELS.NEEDS_MORE_INFO },
  { value: 'RESOLVED', content: QUESTION_STATUS_LABELS.RESOLVED },
  { value: 'CLOSED_DUPLICATE', content: QUESTION_STATUS_LABELS.CLOSED_DUPLICATE },
  { value: 'CLOSED_REJECTED', content: QUESTION_STATUS_LABELS.CLOSED_REJECTED },
];

/**
 * P1-10: Sort options for the dropdown.
 * Format: 'sortBy:sortOrder' to allow combining both in a single value.
 */
interface SortOption {
  value: string;
  content: string;
}

const SORT_OPTIONS: SortOption[] = [
  { value: 'createdAt:desc', content: 'Newest First' },
  { value: 'createdAt:asc', content: 'Oldest First' },
  { value: 'updatedAt:desc', content: 'Recently Updated' },
  { value: 'status:asc', content: 'Status (A-Z)' },
  { value: 'status:desc', content: 'Status (Z-A)' },
];

/**
 * Props for QuestionFilters component.
 */
export interface QuestionFiltersProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (value: string) => void;
  /** Whether unanswered filter is active */
  unansweredOnly: boolean;
  /** Callback when unanswered filter changes */
  onUnansweredChange: (checked: boolean) => void;
  /** Whether has-images filter is active */
  hasImagesOnly: boolean;
  /** Callback when has-images filter changes */
  onHasImagesChange: (checked: boolean) => void;
  /** Whether needs-review filter is active (questions with >= 1 pending reply) */
  needsReviewOnly: boolean;
  /** Callback when needs-review filter changes */
  onNeedsReviewChange: (checked: boolean) => void;
  /** Whether pending-images filter is active (questions with >= 1 image awaiting moderation) */
  pendingImagesOnly: boolean;
  /** Callback when pending-images filter changes */
  onPendingImagesChange: (checked: boolean) => void;
  /** Current status filter value */
  statusFilter: string;
  /** Callback when status filter changes */
  onStatusChange: (value: string) => void;
  /** Current sort value */
  sortValue: string;
  /** Callback when sort changes */
  onSortChange: (value: string) => void;
  /** Freeze queue-shaping controls while a status outcome owns the current view. */
  disabled?: boolean;
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * Filter controls component for the question queue.
 *
 * Provides status filter and sort dropdowns.
 *
 * @example
 * ```tsx
 * <QuestionFilters
 *   statusFilter={statusFilter}
 *   onStatusChange={handleStatusChange}
 *   sortValue={sortValue}
 *   onSortChange={handleSortChange}
 *   testId="queue"
 * />
 * ```
 */
export function QuestionFilters({
  searchQuery,
  onSearchChange,
  unansweredOnly,
  onUnansweredChange,
  hasImagesOnly,
  onHasImagesChange,
  needsReviewOnly,
  onNeedsReviewChange,
  pendingImagesOnly,
  onPendingImagesChange,
  statusFilter,
  onStatusChange,
  sortValue,
  onSortChange,
  disabled = false,
  testId,
}: QuestionFiltersProps): React.ReactElement {
  return (
    <Box padding="medium">
      <Flex justifyContent="flex-start" alignItems="center" flexGap="1rem" flexWrap="wrap">
        <FlexItem style={{ minWidth: '240px', flex: 1, maxWidth: '360px' }}>
          <Input
            iconLeft={<SearchIcon />}
            placeholder="Search questions..."
            aria-label="Search questions"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={disabled}
            data-testid={testId ? `${testId}-search-input` : undefined}
          />
        </FlexItem>
        <FlexItem style={{ width: '200px' }}>
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onOptionChange={onStatusChange}
            placeholder="Filter by status"
            disabled={disabled}
            data-testid={testId ? `${testId}-status-filter` : undefined}
          />
        </FlexItem>
        {/* P1-10: Sort dropdown */}
        <FlexItem style={{ width: '200px' }}>
          <Select
            options={SORT_OPTIONS}
            value={sortValue}
            onOptionChange={onSortChange}
            placeholder="Sort by"
            disabled={disabled}
            data-testid={testId ? `${testId}-sort-select` : undefined}
          />
        </FlexItem>
        <FlexItem>
          <Checkbox
            label="Unanswered only"
            checked={unansweredOnly}
            onChange={() => onUnansweredChange(!unansweredOnly)}
            disabled={disabled}
            data-testid={testId ? `${testId}-unanswered-filter` : undefined}
          />
        </FlexItem>
        <FlexItem>
          <Checkbox
            label="Has images"
            checked={hasImagesOnly}
            onChange={() => onHasImagesChange(!hasImagesOnly)}
            disabled={disabled}
            data-testid={testId ? `${testId}-has-images-filter` : undefined}
          />
        </FlexItem>
        <FlexItem>
          <Checkbox
            label="Needs review"
            checked={needsReviewOnly}
            onChange={() => onNeedsReviewChange(!needsReviewOnly)}
            disabled={disabled}
            data-testid={testId ? `${testId}-needs-review-filter` : undefined}
          />
        </FlexItem>
        <FlexItem>
          <Checkbox
            label="Pending images"
            checked={pendingImagesOnly}
            onChange={() => onPendingImagesChange(!pendingImagesOnly)}
            disabled={disabled}
            data-testid={testId ? `${testId}-pending-images-filter` : undefined}
          />
        </FlexItem>
      </Flex>
    </Box>
  );
}
